/**
 * First-run setup.
 *
 * Ten steps, and the operator can leave at any point. The step index is written
 * to local storage on every change and re-read on mount, so a browser crash
 * three hosts into the process costs three hosts, not the whole run. The server
 * is asked first — if `/setup/status` knows where the install got to, that wins
 * over the local guess, because the owner account and the hosts live there and
 * the browser's opinion about them is only a cache.
 *
 * The dependency step is live rather than a snapshot. Setting Kimene up usually
 * means fixing something on the machine in another window; a health panel that
 * only refreshes when you press a button turns that into a guessing game, so it
 * polls while the step is open and unblocks itself the moment the fix lands.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type PageResponse } from '@/api/client'
import { Panel, Badge, Button, StatusDot, Spinner, Meter } from '@/components/primitives'
import {
  QueryState,
  TextField,
  SelectField,
  CheckboxField,
  Notice,
  useApiErrorMessage,
} from '@/components/controls'
import { AddHostDialog } from './HostsPage'
import { Logo } from '@/components/Logo'
import { useT, useLocale } from '@/i18n/context'
import { formatRelative } from '@/i18n'
import {
  accountStateTone,
  confidenceTone,
  dependencyLabel,
  dependencyTone,
  healthTone,
} from '@/lib/tone'
import type { AccountProfile, Host, Project, RoutingMode, SystemHealth } from '@/types/domain'
import './SetupWizard.css'

const STEPS = [
  'owner',
  'dependencies',
  'controlHost',
  'hosts',
  'node',
  'accounts',
  'projects',
  'routing',
  'orchestrator',
  'finalCheck',
] as const
type StepId = (typeof STEPS)[number]

const STEP_LABEL_KEY = {
  owner: 'setup.step.owner',
  dependencies: 'setup.step.dependencies',
  controlHost: 'setup.step.controlHost',
  hosts: 'setup.step.hosts',
  node: 'setup.step.node',
  accounts: 'setup.step.accounts',
  projects: 'setup.step.projects',
  routing: 'setup.step.routing',
  orchestrator: 'setup.step.orchestrator',
  finalCheck: 'setup.step.finalCheck',
} as const

/** Steps the operator may pass without doing anything. Owner, dependencies and
 *  the final check are not among them: an install without an owner has nobody
 *  to sign in, and one with a failing dependency does not work. */
const SKIPPABLE: StepId[] = ['hosts', 'node', 'accounts', 'projects']

const STORAGE_KEY = 'kimene.setup.step'
const MIN_PASSWORD = 12

/**
 * Mirrors api.SetupStatusResponse exactly.
 *
 * The previous shape was invented: it declared `ownerCreated` and `step`, which
 * no handler sends. Both read as undefined, so the sync below silently never
 * fired — and after a reload the wizard offered the owner step again to an
 * install that already had an owner, which the server then refused with
 * "setup has already been completed".
 *
 * There is no `step` field and there should not be: the server records whether
 * an owner exists and whether setup finished, and the wizard derives its
 * position from those two facts rather than the server tracking a screen.
 */
interface SetupStatus {
  available: boolean
  bootstrapCompleted: boolean
  tokenOutstanding: boolean
  tokenExpiresAt?: string
  setupCompleted: boolean
  csrfToken?: string
}

interface RoutingDefaults {
  mode: RoutingMode
  allowProviderSwitch: boolean
  allowModelSwitch: boolean
  requireSafeCheckpointForSwitch: boolean
}

interface OrchestratorPolicy {
  autoApproveBelowRisk: string
  maxConcurrentTasks: number
  pauseOnRepeatedFailure: boolean
}

export function SetupWizard({ onComplete }: { onComplete: () => void }) {
  const t = useT()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()

  const [index, setIndex] = useState(() => {
    const stored = Number(localStorage.getItem(STORAGE_KEY) ?? '0')
    return Number.isInteger(stored) && stored >= 0 && stored < STEPS.length ? stored : 0
  })
  const [resumed] = useState(() => Number(localStorage.getItem(STORAGE_KEY) ?? '0') > 0)
  const [ownerCreated, setOwnerCreated] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [routing, setRouting] = useState<RoutingDefaults>({
    mode: 'AUTO_NOTIFY',
    allowProviderSwitch: true,
    allowModelSwitch: true,
    requireSafeCheckpointForSwitch: true,
  })
  const [policy, setPolicy] = useState<OrchestratorPolicy>({
    autoApproveBelowRisk: 'L2_VALIDATED_CHANGE',
    maxConcurrentTasks: 4,
    pauseOnRepeatedFailure: true,
  })

  const status = useQuery({
    queryKey: ['setup-status'],
    queryFn: () => api.get<SetupStatus>('/setup/status'),
  })

  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<SystemHealth>('/health'),
    // Live only while the dependency step is open; polling a health endpoint
    // for the whole wizard is noise the server does not need.
    refetchInterval: STEPS[index] === 'dependencies' ? 5000 : false,
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(index))
  }, [index])

  // The server's view wins. It knows whether an owner exists; local storage
  // only remembers which screen was last on display, and a crash or a reload
  // resets React state while the account it created stays very much created.
  useEffect(() => {
    const s = status.data
    if (!s) return
    if (s.bootstrapCompleted) {
      setOwnerCreated(true)
      // Never sit on the owner step once an owner exists: submitting it again
      // is refused, and the refusal reads as a fault rather than as "this is
      // already done".
      setIndex((prev) => Math.max(prev, 1))
    }
    if (s.setupCompleted) {
      localStorage.removeItem(STORAGE_KEY)
      onComplete()
    }
  }, [status.data, onComplete])

  const complete = useMutation({
    mutationFn: () => api.post<void>('/setup/complete', { routing, orchestrator: policy }),
    onSuccess: () => {
      localStorage.removeItem(STORAGE_KEY)
      void queryClient.invalidateQueries({ queryKey: ['setup-status'] })
      onComplete()
    },
    onError: (err) => setError(describe(err).title || t('setup.stepFailed')),
  })

  const step = STEPS[index] ?? 'owner'
  // Only a critical dependency being down blocks the wizard. A degraded
  // non-critical one is reported and stepped past — refusing to let an operator
  // finish setup because telemetry is stale would be its own kind of wrong.
  const failingDependency =
    health.data?.dependencies?.some((d) => !d.up && d.critical) ?? false

  const blocked =
    (step === 'owner' && !ownerCreated) || (step === 'dependencies' && failingDependency)

  const goBack = () => setIndex((i) => Math.max(0, i - 1))
  const goNext = () => setIndex((i) => Math.min(STEPS.length - 1, i + 1))

  return (
    <main id="main" className="k-setup" tabIndex={-1}>
      <div className="k-setup__frame">
        <header className="k-setup__head">
          <Logo className="k-setup__mark" size={34} />
          <div>
            <h1 className="k-setup__title">{t('setup.title')}</h1>
            <p className="k-setup__sub">{t('setup.subtitle')}</p>
          </div>
          {/* The wizard's only live region, and deliberately this small. It used
              to be the whole step panel, which meant every keystroke in the
              password field re-announced the entire step — the classic way a
              live region turns into noise a screen-reader user switches off.
              What actually changes, and is worth announcing, is which step you
              are on. */}
          <span className="k-mono k-setup__counter" role="status">
            {t('setup.stepOf', { current: index + 1, total: STEPS.length })}
            <span className="k-visually-hidden"> · {t(STEP_LABEL_KEY[step])}</span>
          </span>
        </header>

        {resumed && index > 0 && <Notice tone="info">{t('setup.resume')}</Notice>}
        {error && <Notice tone="err">{error}</Notice>}

        <div className="k-setup__layout">
          <nav className="k-setup__steps" aria-label={t('setup.title')}>
            <ol>
              {STEPS.map((s, i) => (
                <li key={s}>
                  <button
                    type="button"
                    className={`k-setup__step ${i === index ? 'k-setup__step--on' : ''} ${
                      i < index ? 'k-setup__step--done' : ''
                    }`}
                    aria-current={i === index ? 'step' : undefined}
                    // Forward jumps are refused: a later step reads state the
                    // earlier ones create, and arriving there early shows an
                    // empty screen that looks like a bug.
                    disabled={i > index}
                    onClick={() => setIndex(i)}
                  >
                    <span className="k-mono k-setup__num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="k-setup__steplabel">{t(STEP_LABEL_KEY[s])}</span>
                    {i < index && (
                      <span className="k-setup__tick" aria-hidden>
                        ✓
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ol>
          </nav>

          <section className="k-setup__panel" aria-label={t(STEP_LABEL_KEY[step])}>
            {step === 'owner' && (
              <OwnerStep
                created={ownerCreated}
                onCreated={() => {
                  setOwnerCreated(true)
                  goNext()
                }}
              />
            )}
            {step === 'dependencies' && <DependenciesStep query={health} />}
            {step === 'controlHost' && <ControlHostStep />}
            {step === 'hosts' && <HostsStep />}
            {step === 'node' && <NodeStep />}
            {step === 'accounts' && <AccountsStep />}
            {step === 'projects' && <ProjectsStep />}
            {step === 'routing' && <RoutingStep value={routing} onChange={setRouting} />}
            {step === 'orchestrator' && <PolicyStep value={policy} onChange={setPolicy} />}
            {step === 'finalCheck' && (
              <FinalStep health={health.data} routing={routing} policy={policy} />
            )}
          </section>
        </div>

        <footer className="k-setup__foot">
          <Button variant="ghost" onClick={goBack} disabled={index === 0}>
            ← {t('action.back')}
          </Button>

          <div className="k-setup__footright">
            {SKIPPABLE.includes(step) && (
              <Button variant="ghost" onClick={goNext}>
                {t('setup.skip')}
              </Button>
            )}
            {step === 'finalCheck' ? (
              <Button variant="primary" busy={complete.isPending} onClick={() => complete.mutate()}>
                {t('setup.finish')}
              </Button>
            ) : (
              <Button variant="primary" onClick={goNext} disabled={blocked}>
                {t('action.next')} →
              </Button>
            )}
          </div>
        </footer>
      </div>
    </main>
  )
}

/* ------------------------------------------------------------------ Owner */

function OwnerStep({ created, onCreated }: { created: boolean; onCreated: () => void }) {
  const t = useT()
  const describe = useApiErrorMessage()

  const [token, setToken] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    // The one-time token is what authorises this call. The control plane has no
    // account to authenticate against yet, so possession of the token minted on
    // its own console is the only proof the caller is the operator. Omitting it
    // makes the server refuse with a permission error, which reads as a
    // configuration fault rather than a missing field — so the form has to ask
    // for it, and the hint has to say where it comes from.
    mutationFn: () =>
      api.post<void>('/setup/owner', {
        token: token.trim(),
        username: username.trim(),
        email: email.trim() || undefined,
        displayName: username.trim(),
        password,
      }),
    onSuccess: onCreated,
    onError: (err) => {
      const { title, hint } = describe(err)
      // Both halves: the title says what was refused, the hint says what to do
      // about it. Showing only the title is what turned "that setup token was
      // not accepted" into a bare permission error.
      setError(hint && hint !== title ? `${title} — ${hint}` : title)
    },
  })

  if (created) {
    return (
      <StepShell titleKey="setup.step.owner" hintKey="setup.ownerHint">
        <Notice tone="ok">{t('setup.ownerCreated')}</Notice>
      </StepShell>
    )
  }

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD
  const mismatch = confirm.length > 0 && confirm !== password
  const valid =
    token.trim() !== '' &&
    username.trim() !== '' &&
    password.length >= MIN_PASSWORD &&
    confirm === password

  return (
    <StepShell titleKey="setup.step.owner" hintKey="setup.ownerHint">
      {error && <Notice tone="err">{error}</Notice>}
      <Notice tone="neutral">{t('setup.tokenHint')}</Notice>
      <TextField
        label={t('setup.bootstrapToken')}
        value={token}
        onChange={setToken}
        autoComplete="off"
        required
      />
      <TextField
        label={t('auth.username')}
        value={username}
        onChange={setUsername}
        autoComplete="username"
        required
      />
      <TextField
        label={t('setup.ownerEmail')}
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
      />
      <TextField
        label={t('auth.password')}
        type="password"
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
        required
        error={tooShort ? t('setup.passwordTooShort') : undefined}
      />
      <TextField
        label={t('setup.confirmPassword')}
        type="password"
        value={confirm}
        onChange={setConfirm}
        autoComplete="new-password"
        required
        error={mismatch ? t('setup.passwordMismatch') : undefined}
      />
      <Button
        variant="primary"
        busy={create.isPending}
        disabled={!valid}
        onClick={() => create.mutate()}
      >
        {t('action.create')}
      </Button>
    </StepShell>
  )
}

/* ----------------------------------------------------------- Dependencies */

function DependenciesStep({
  query,
}: {
  query: {
    data?: SystemHealth
    isPending: boolean
    error: unknown
    refetch: () => unknown
  }
}) {
  const t = useT()
  const health = query.data
  // The optional chain has to cover `dependencies` too, not just `health`.
  // Stopping at `health` is what threw here: the server omits the array for an
  // unauthenticated caller, and the wizard runs before any account exists.
  const deps = health?.dependencies ?? []
  // Only a *critical* dependency being down blocks setup. A degraded
  // non-critical one is worth showing and not worth stopping for.
  const blocking = deps.filter((d) => !d.up && d.critical)

  return (
    <StepShell titleKey="setup.step.dependencies" hintKey="setup.dependenciesHint">
      <QueryState
        pending={query.isPending}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {health && (
          <>
            {blocking.length > 0 && <Notice tone="err">{t('setup.blockedByHealth')}</Notice>}

            {deps.length > 0 ? (
              <ul className="k-setupchecks">
                {deps.map((d) => (
                  <li className="k-setupchecks__row" key={d.name}>
                    <StatusDot tone={dependencyTone(d)} label={dependencyLabel(d)} />
                    <span className="k-setupchecks__name">{d.name}</span>
                    <Badge tone={dependencyTone(d)} subtle>
                      {dependencyLabel(d)}
                    </Badge>
                    <span className="k-setupchecks__detail">{d.detail ?? ''}</span>
                    <span className="k-mono k-setupchecks__latency">
                      {d.latencyMs > 0 ? `${Math.round(d.latencyMs)} ms` : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              // Not an error, and not "all clear" either. The detail is withheld
              // from anonymous callers on purpose; saying so is more honest than
              // an empty list that reads as nothing to report.
              <Notice tone="neutral">{t('setup.dependencyDetailHidden')}</Notice>
            )}

            <p className="k-setup__note">
              <Badge tone={healthTone(health.status)} subtle>
                {health.status}
              </Badge>
              <span className="k-mono"> {health.version}</span>
            </p>
          </>
        )}
      </QueryState>
    </StepShell>
  )
}

/* ------------------------------------------------------------ Control host */

function ControlHostStep() {
  const t = useT()
  const hosts = useQuery({
    queryKey: ['hosts'],
    queryFn: () => api.get<PageResponse<Host>>('/hosts'),
  })

  const control = hosts.data?.items.find((h) => h.isControlHost)

  return (
    <StepShell titleKey="setup.step.controlHost" hintKey="setup.controlHostHint">
      <QueryState
        pending={hosts.isPending}
        error={hosts.error}
        onRetry={() => void hosts.refetch()}
      >
        {control ? (
          <div className="k-setuphost">
            <StatusDot tone={control.status === 'ONLINE' ? 'ok' : 'warn'} label={control.status} />
            <span className="k-setuphost__name">{control.name}</span>
            <Badge tone="accent" subtle>
              {t('host.controlHost')}
            </Badge>
            <span className="k-mono k-setuphost__addr">
              {control.user}@{control.address}:{control.port}
            </span>
          </div>
        ) : (
          <Notice tone="warn">{t('host.emptyHint')}</Notice>
        )}
      </QueryState>
    </StepShell>
  )
}

/* ------------------------------------------------------------------ Hosts */

function HostsStep() {
  const t = useT()
  const [adding, setAdding] = useState(false)

  const hosts = useQuery({
    queryKey: ['hosts'],
    queryFn: () => api.get<PageResponse<Host>>('/hosts'),
  })

  const items = hosts.data?.items ?? []

  return (
    <StepShell titleKey="setup.step.hosts" hintKey="setup.hostsHint">
      <QueryState
        pending={hosts.isPending}
        error={hosts.error}
        onRetry={() => void hosts.refetch()}
        empty={items.length === 0}
        emptyTitle={t('setup.nothingYet')}
        emptyHint={t('host.emptyHint')}
        emptyAction={
          <Button variant="primary" onClick={() => setAdding(true)}>
            {t('host.add')}
          </Button>
        }
      >
        <ul className="k-setuplist">
          {items.map((h) => (
            <li className="k-setuplist__row" key={h.id}>
              <StatusDot tone={h.status === 'ONLINE' ? 'ok' : 'warn'} label={h.status} />
              <span className="k-setuplist__name">{h.name}</span>
              <span className="k-mono k-setuplist__meta">
                {h.user}@{h.address}
              </span>
              <Badge tone="neutral" subtle>
                {h.status}
              </Badge>
            </li>
          ))}
        </ul>
      </QueryState>

      <Button onClick={() => setAdding(true)}>{t('host.add')}</Button>
      {adding && <AddHostDialog onClose={() => setAdding(false)} />}
    </StepShell>
  )
}

/* ------------------------------------------------------------------- Node */

function NodeStep() {
  const t = useT()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  const [error, setError] = useState<string | null>(null)

  const hosts = useQuery({
    queryKey: ['hosts'],
    queryFn: () => api.get<PageResponse<Host>>('/hosts'),
  })

  const bootstrap = useMutation({
    mutationFn: (hostId: string) => api.post<void>(`/hosts/${hostId}/bootstrap`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['hosts'] }),
    onError: (err) => setError(describe(err).title),
  })

  const items = hosts.data?.items ?? []

  return (
    <StepShell titleKey="setup.step.node" hintKey="setup.nodeHint">
      {error && <Notice tone="err">{error}</Notice>}
      <QueryState
        pending={hosts.isPending}
        error={hosts.error}
        onRetry={() => void hosts.refetch()}
        empty={items.length === 0}
        emptyTitle={t('setup.nothingYet')}
        emptyHint={t('host.emptyHint')}
      >
        <ul className="k-setuplist">
          {items.map((h) => (
            <li className="k-setuplist__row" key={h.id}>
              <StatusDot tone={h.nodeStatus === 'CONNECTED' ? 'ok' : 'warn'} label={h.nodeStatus} />
              <span className="k-setuplist__name">{h.name}</span>
              <Badge tone={h.nodeStatus === 'CONNECTED' ? 'ok' : 'neutral'} subtle>
                {h.nodeStatus}
              </Badge>
              <Button
                size="sm"
                disabled={h.nodeStatus === 'CONNECTED'}
                busy={bootstrap.isPending && bootstrap.variables === h.id}
                onClick={() => bootstrap.mutate(h.id)}
              >
                {t('host.bootstrap')}
              </Button>
            </li>
          ))}
        </ul>
      </QueryState>
    </StepShell>
  )
}

/* --------------------------------------------------------------- Accounts */

function AccountsStep() {
  const t = useT()
  const { locale } = useLocale()

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () =>
      api.get<
        PageResponse<
          AccountProfile & {
            capacity?: {
              confidence: 'EXACT' | 'ESTIMATED' | 'INFERRED' | 'UNKNOWN'
            }
          }
        >
      >('/accounts'),
  })

  const items = accounts.data?.items ?? []

  return (
    <StepShell titleKey="setup.step.accounts" hintKey="setup.accountsHint">
      <QueryState
        pending={accounts.isPending}
        error={accounts.error}
        onRetry={() => void accounts.refetch()}
        empty={items.length === 0}
        emptyTitle={t('setup.nothingYet')}
        emptyHint={t('account.emptyHint')}
      >
        <ul className="k-setuplist">
          {items.map((a) => (
            <li className="k-setuplist__row" key={a.id}>
              <StatusDot tone={accountStateTone(a.state)} label={a.state} />
              <span className="k-setuplist__name">{a.label}</span>
              <Badge tone="neutral" subtle mono>
                {a.provider}
              </Badge>
              <Badge tone={accountStateTone(a.state)} subtle>
                {a.state}
              </Badge>
              <Badge tone={confidenceTone(a.capacity?.confidence ?? 'UNKNOWN')} subtle>
                {t(`confidence.${a.capacity?.confidence ?? 'UNKNOWN'}`)}
              </Badge>
              <span className="k-setuplist__meta">{formatRelative(a.lastProbeAt, locale)}</span>
            </li>
          ))}
        </ul>
      </QueryState>
    </StepShell>
  )
}

/* --------------------------------------------------------------- Projects */

function ProjectsStep() {
  const t = useT()

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<PageResponse<Project>>('/projects'),
  })

  const items = projects.data?.items ?? []

  return (
    <StepShell titleKey="setup.step.projects" hintKey="setup.projectsHint">
      <QueryState
        pending={projects.isPending}
        error={projects.error}
        onRetry={() => void projects.refetch()}
        empty={items.length === 0}
        emptyTitle={t('setup.nothingYet')}
        emptyHint={t('project.emptyHint')}
      >
        <ul className="k-setuplist">
          {items.map((p) => (
            <li className="k-setuplist__row" key={p.id}>
              <StatusDot tone={p.state === 'ACTIVE' ? 'ok' : 'neutral'} label={p.state} />
              <span className="k-setuplist__name">{p.name}</span>
              <span className="k-mono k-setuplist__meta">{p.slug}</span>
              <Badge tone="neutral" subtle>
                {p.repositories?.length ?? 0} {t('project.repositories')}
              </Badge>
            </li>
          ))}
        </ul>
      </QueryState>
    </StepShell>
  )
}

/* ---------------------------------------------------------------- Routing */

function RoutingStep({
  value,
  onChange,
}: {
  value: RoutingDefaults
  onChange: (v: RoutingDefaults) => void
}) {
  const t = useT()
  return (
    <StepShell titleKey="setup.step.routing" hintKey="setup.routingHint">
      <SelectField
        label={t('routing.mode')}
        value={value.mode}
        onChange={(v) => onChange({ ...value, mode: v as RoutingMode })}
        options={[
          { value: 'AUTO', label: 'AUTO' },
          { value: 'AUTO_NOTIFY', label: 'AUTO_NOTIFY' },
          { value: 'ASK_BEFORE_SWITCH', label: 'ASK_BEFORE_SWITCH' },
          { value: 'PINNED_PROVIDER', label: 'PINNED_PROVIDER' },
          { value: 'PINNED_MODEL_FAMILY', label: 'PINNED_MODEL_FAMILY' },
          { value: 'PINNED_ACCOUNT', label: 'PINNED_ACCOUNT' },
          { value: 'MANUAL', label: 'MANUAL' },
        ]}
      />
      <CheckboxField
        label={t('account.provider')}
        checked={value.allowProviderSwitch}
        onChange={(v) => onChange({ ...value, allowProviderSwitch: v })}
      />
      <CheckboxField
        label={t('task.model')}
        checked={value.allowModelSwitch}
        onChange={(v) => onChange({ ...value, allowModelSwitch: v })}
      />
      <CheckboxField
        label={t('cc.lastCheckpoint')}
        checked={value.requireSafeCheckpointForSwitch}
        onChange={(v) => onChange({ ...value, requireSafeCheckpointForSwitch: v })}
      />
    </StepShell>
  )
}

/* ----------------------------------------------------------- Orchestrator */

function PolicyStep({
  value,
  onChange,
}: {
  value: OrchestratorPolicy
  onChange: (v: OrchestratorPolicy) => void
}) {
  const t = useT()
  return (
    <StepShell titleKey="setup.step.orchestrator" hintKey="setup.orchestratorHint">
      <SelectField
        label={t('project.riskLevel')}
        value={value.autoApproveBelowRisk}
        onChange={(v) => onChange({ ...value, autoApproveBelowRisk: v })}
        options={[
          { value: 'L0_READ_ONLY', label: t('risk.L0_READ_ONLY') },
          { value: 'L1_ISOLATED_WRITE', label: t('risk.L1_ISOLATED_WRITE') },
          {
            value: 'L2_VALIDATED_CHANGE',
            label: t('risk.L2_VALIDATED_CHANGE'),
          },
          {
            value: 'L3_EXTERNAL_OR_IRREVERSIBLE',
            label: t('risk.L3_EXTERNAL_OR_IRREVERSIBLE'),
          },
        ]}
      />
      <TextField
        label={t('account.concurrency')}
        value={String(value.maxConcurrentTasks)}
        onChange={(v) => {
          const n = Number(v.replace(/\D/g, ''))
          onChange({
            ...value,
            maxConcurrentTasks: Number.isFinite(n) && n > 0 ? n : 1,
          })
        }}
        inputMode="numeric"
        mono
      />
      <CheckboxField
        label={t('settings.notifyFailures')}
        checked={value.pauseOnRepeatedFailure}
        onChange={(v) => onChange({ ...value, pauseOnRepeatedFailure: v })}
      />
    </StepShell>
  )
}

/* ------------------------------------------------------------ Final check */

function FinalStep({
  health,
  routing,
  policy,
}: {
  health?: SystemHealth
  routing: RoutingDefaults
  policy: OrchestratorPolicy
}) {
  const t = useT()

  const hosts = useQuery({
    queryKey: ['hosts'],
    queryFn: () => api.get<PageResponse<Host>>('/hosts'),
  })
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<PageResponse<AccountProfile>>('/accounts'),
  })
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<PageResponse<Project>>('/projects'),
  })

  const readiness = useMemo(() => {
    const checks = [
      // The backend reports 'ok' or 'degraded', never 'FAIL', and undefined is
      // not 'FAIL' either — so `!== 'FAIL'` was always true and a degraded or
      // still-loading backend scored as a pass while the health badge below it
      // read 'degraded'. Require health loaded and ok.
      health?.status === 'ok',
      (hosts.data?.items.length ?? 0) > 0,
      (accounts.data?.items.length ?? 0) > 0,
      (projects.data?.items.length ?? 0) > 0,
    ]
    return checks.filter(Boolean).length / checks.length
  }, [health, hosts.data, accounts.data, projects.data])

  return (
    <StepShell titleKey="setup.step.finalCheck" hintKey="setup.finalCheckHint">
      <div className="k-setupsummary">
        <Meter
          value={readiness}
          tone={readiness === 1 ? 'ok' : 'warn'}
          ariaLabel={t('setup.step.finalCheck')}
        />
        <dl className="k-facts">
          <SummaryFact
            label={t('cc.systemHealth')}
            value={
              health ? (
                <Badge tone={healthTone(health.status)} subtle>
                  {health.status}
                </Badge>
              ) : (
                <Spinner size={12} />
              )
            }
          />
          <SummaryFact label={t('nav.hosts')} value={String(hosts.data?.items.length ?? 0)} />
          <SummaryFact label={t('nav.accounts')} value={String(accounts.data?.items.length ?? 0)} />
          <SummaryFact label={t('nav.projects')} value={String(projects.data?.items.length ?? 0)} />
          <SummaryFact label={t('routing.mode')} value={routing.mode} />
          <SummaryFact label={t('account.concurrency')} value={String(policy.maxConcurrentTasks)} />
        </dl>
      </div>
    </StepShell>
  )
}

function SummaryFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="k-fact">
      <dt className="k-fact__key">{label}</dt>
      <dd className="k-fact__val k-mono">{value}</dd>
    </div>
  )
}

/* ------------------------------------------------------------ Step shell */

function StepShell({
  titleKey,
  hintKey,
  children,
}: {
  titleKey: Parameters<ReturnType<typeof useT>>[0]
  hintKey: Parameters<ReturnType<typeof useT>>[0]
  children: ReactNode
}) {
  const t = useT()
  return (
    <Panel title={t(titleKey)}>
      <div className="k-setupstep">
        <p className="k-setupstep__hint">{t(hintKey)}</p>
        {children}
      </div>
    </Panel>
  )
}
